import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import styles from "./DemoPage.module.css";
import c from "./ClinicalCase/ClinicalCase.module.css";
import PathwayGraph from "./ClinicalCase/PathwayGraph";

const pathway = {
  nodes: [
    {
      id: "n_polg",
      label: "Nuclear POLG defect (mtDNA maintenance)",
      type: "genotype",
      tier: "inferred",
      anchor: "hypothesis:h1",
    },
    {
      id: "n_mtdna",
      label: "mtDNA instability / multiple deletions",
      type: "mechanism",
      tier: "established",
      anchor: "paper:38391710",
    },
    {
      id: "n_oxphos",
      label: "Respiratory chain / OXPHOS impairment",
      type: "mechanism",
      tier: "established",
      anchor: "paper:chunked_mapping",
    },
    {
      id: "n_eom",
      label: "Extraocular muscle bioenergetic failure",
      type: "tissue",
      tier: "inferred",
      anchor: "finding:cpeo",
    },
    {
      id: "n_cpeo",
      label: "CPEO + bilateral ptosis",
      type: "phenotype",
      tier: "established",
      anchor: "paper:38391710",
    },
    {
      id: "n_drg",
      label: "Dorsal root ganglion vulnerability",
      type: "tissue",
      tier: "hypothesis",
      anchor: "finding:sensory",
    },
    {
      id: "n_ganglio",
      label: "Non-length-dependent sensory ganglionopathy",
      type: "phenotype",
      tier: "established",
      anchor: "paper:28794150",
    },
    {
      id: "n_snpc",
      label: "Substantia nigra dopaminergic fragility",
      type: "tissue",
      tier: "inferred",
      anchor: "finding:parkinsonism",
    },
    {
      id: "n_park",
      label: "Levodopa-poor parkinsonism + resting tremor",
      type: "phenotype",
      tier: "established",
      anchor: "paper:chunked_structural",
    },
    {
      id: "n_cortex",
      label: "Cortical energy failure",
      type: "tissue",
      tier: "inferred",
      anchor: "finding:seizure",
    },
    {
      id: "n_seizure",
      label: "Seizures + transient confusion",
      type: "phenotype",
      tier: "established",
      anchor: "paper:32391929",
    },
    {
      id: "n_cox",
      label: "Excess COX-negative fibres (no ragged-red)",
      type: "phenotype",
      tier: "inferred",
      anchor: "finding:biopsy",
    },
    {
      id: "n_dx",
      label: "POLG-related mitochondrial disease (CPEO-plus)",
      type: "diagnosis",
      tier: "inferred",
      anchor: "hypothesis:h1",
    },
  ],
  edges: [
    {
      source: "n_polg",
      target: "n_mtdna",
      tier: "established",
      evidence:
        "CPEO is defined by progressive restriction of eye movements and ptosis due to extraocular muscle weakness, frequently arising from mtDNA instability.",
      anchor: "paper:38391710",
    },
    {
      source: "n_mtdna",
      target: "n_oxphos",
      tier: "established",
      evidence:
        "Cytochrome c oxidase contains mtDNA-encoded subunits, making respiratory chain function highly sensitive to mtDNA deletions or depletion.",
      anchor: "paper:chunked_mapping",
    },
    {
      source: "n_oxphos",
      target: "n_eom",
      tier: "inferred",
      evidence:
        "Extraocular muscles have exceptionally high mitochondrial density and rely almost entirely on oxidative phosphorylation; as post-mitotic tissue they cannot dilute defective genomes by cell division, so pathogenic mtDNA accumulates over decades.",
      anchor: "finding:cpeo",
    },
    {
      source: "n_eom",
      target: "n_cpeo",
      tier: "established",
      evidence:
        "Progressive external ophthalmoplegia and ptosis arise from this bioenergetic vulnerability of extraocular muscle.",
      anchor: "paper:38391710",
    },
    {
      source: "n_oxphos",
      target: "n_drg",
      tier: "hypothesis",
      evidence:
        "If mtDNA maintenance defects preferentially impair dorsal root ganglion somatic metabolism rather than distal axonal transport, a selective sensory ganglionopathy could emerge. Prediction: sural nerve biopsy would show vacuolated DRG satellite cells with elevated 4-HNE and reduced mitochondrial membrane potential, independent of distal axonal degeneration.",
      anchor: "finding:sensory",
    },
    {
      source: "n_drg",
      target: "n_ganglio",
      tier: "established",
      evidence:
        "Peripheral neuropathy is a common manifestation of complex inherited mitochondrial disease and may be a major phenotypic component.",
      anchor: "paper:28794150",
    },
    {
      source: "n_oxphos",
      target: "n_snpc",
      tier: "inferred",
      evidence:
        "Substantia nigra dopaminergic neurons have high basal ROS from dopamine metabolism and iron accumulation, making them uniquely vulnerable to respiratory chain impairment; the levodopa-poor parkinsonism reflects network dysfunction from chronic energy deficit rather than alpha-synuclein pathology.",
      anchor: "finding:parkinsonism",
    },
    {
      source: "n_snpc",
      target: "n_park",
      tier: "established",
      evidence:
        "POLG mutations are associated with parkinsonism co-segregating with CPEO, reflecting basal ganglia susceptibility to mitochondrial dysfunction.",
      anchor: "paper:chunked_structural",
    },
    {
      source: "n_oxphos",
      target: "n_cortex",
      tier: "inferred",
      evidence:
        "Cortical excitability depends on ATP-driven Na+/K+ ATPase and astrocytic glutamate clearance; intermittent cortical energy failure lowers the seizure threshold.",
      anchor: "finding:seizure",
    },
    {
      source: "n_cortex",
      target: "n_seizure",
      tier: "established",
      evidence:
        "Seizures are a major clinical feature in POLG-related disease across multiple age-stratified cohorts.",
      anchor: "paper:32391929",
    },
    {
      source: "n_oxphos",
      target: "n_cox",
      tier: "inferred",
      evidence:
        "COX-negative fibres indicate focal mtDNA instability in scattered myofibers; absence of ragged-red fibres despite COX deficiency fits late-onset, slowly progressive maintenance disorders, and normal bulk spectrophotometry reflects mosaic distribution where unaffected fibres compensate.",
      anchor: "finding:biopsy",
    },
    {
      source: "n_cpeo",
      target: "n_dx",
      tier: "inferred",
      evidence:
        "The assembly of CPEO with sensory ganglionopathy, parkinsonism, seizures and mosaic COX-negative myopathy in an adult converges on a nuclear mtDNA-maintenance defect, most consistently POLG.",
      anchor: "hypothesis:h1",
    },
    {
      source: "n_ganglio",
      target: "n_dx",
      tier: "inferred",
      evidence:
        "The non-length-dependent sensory neuronopathy is a recognised discriminator pointing toward POLG rather than diabetic (length-dependent) neuropathy.",
      anchor: "hypothesis:h1",
    },
    {
      source: "n_cox",
      target: "n_dx",
      tier: "inferred",
      evidence:
        "Excess COX-negative fibres beyond age expectation, with preserved bulk activity, is the muscle signature of a mosaic mtDNA-maintenance defect.",
      anchor: "hypothesis:h1",
    },
  ],
  ungrounded: [],
  anchor_registry: {
    "finding:cpeo":
      "sign: severe external ophthalmoplegia with bilateral ptosis",
    "finding:sensory":
      "investigation: absent sensory nerve action potentials, normal motor conduction (ganglionopathy)",
    "finding:parkinsonism":
      "sign: levodopa-poor parkinsonism with asymmetric resting tremor",
    "finding:seizure": "history: transient confusion and witnessed seizure",
    "finding:biopsy":
      "investigation: excess COX-negative fibres for age, no ragged-red fibres, normal bulk respiratory chain activity",
    "hypothesis:h1":
      "hypothesis: POLG-related mitochondrial disease (CPEO-plus)",
    "paper:38391710":
      "paper: Chronic progressive external ophthalmoplegia and mtDNA instability",
    "paper:28794150":
      "paper: Peripheral neuropathy in inherited mitochondrial disease",
    "paper:32391929":
      "paper: Seizures in POLG-related disease across age cohorts",
    "paper:chunked_mapping":
      "paper: Mapping pathogenic mutations into functional modules (Complex IV / mtDNA sensitivity)",
    "paper:chunked_structural":
      "paper: Structural and molecular basis for mitochondrial parkinsonism",
  },
};

const presentation = [
  {
    label: "Patient",
    value:
      "72-year-old man. Three-year history of slowly progressive, multi-system neurological decline. Extensive workup excludes autoimmune, paraneoplastic, toxic, and nutritional etiologies.",
  },
  {
    label: "Core features",
    value:
      "Severe bilateral external ophthalmoplegia with ptosis, mild proximal lower-limb weakness, mild gait ataxia, and a non-length-dependent sensory neuronopathy (generalised absence of sensory nerve action potentials with preserved motor conduction).",
  },
  {
    label: "Additional",
    value:
      "Mild dysarthria and dysphagia, asymmetric pill-rolling resting tremor with bradykinesia, and recent episodes of transient confusion with one witnessed seizure.",
  },
  {
    label: "MRI",
    value: "Only mild scattered periventricular white matter changes.",
  },
  {
    label: "Biopsy",
    value:
      "Deltoid muscle biopsy: oxidative stress markers and COX-negative fibers exceeding age expectations, but no ragged-red fibers and normal bulk mitochondrial respiratory chain complex activity.",
  },
  { label: "Genetics", value: "No genetic testing has been performed." },
  {
    label: "Question",
    value:
      "The team seeks a unifying diagnosis and guidance on the safety of initiating sodium valproate for seizure prophylaxis.",
  },
];

const mechanism = [
  {
    tier: "ESTABLISHED",
    text: "Extraocular muscles possess exceptionally high mitochondrial density and rely almost exclusively on oxidative phosphorylation for sustained contractile function. Chronic progressive external ophthalmoplegia is defined by progressive restriction of eye movements and ptosis, frequently arising from mtDNA instability.",
    source: "PMID 38391710.",
  },
  {
    tier: "INFERRED",
    text: "The severe limitation of eye movements and bilateral ptosis follows from the heightened bioenergetic vulnerability of extraocular muscle to mtDNA copy number reduction or multiple deletions. As post-mitotic tissue, these muscles cannot dilute defective genomes through cell division, allowing pathogenic mtDNA to accumulate and impair respiratory chain assembly over decades.",
  },
  {
    tier: "ESTABLISHED",
    text: "Peripheral neuropathy is a common manifestation in complex inherited mitochondrial disease and may present as a major phenotypic component.",
    source: "PMID 28794150.",
  },
  {
    tier: "HYPOTHESIS",
    text: "While POLG-associated neuropathies are classically length-dependent and mixed sensorimotor, dorsal root ganglion neurons are post-mitotic, metabolically active, and highly susceptible to oxidative stress. If mtDNA maintenance defects preferentially impair DRG somatic metabolism rather than distal axonal transport, a selective sensory ganglionopathy pattern could emerge.",
    prediction:
      "Immunohistochemical staining of sural nerve biopsies would reveal swollen, vacuolated DRG satellite cells with elevated 4-HNE and reduced mitochondrial membrane potential, independent of distal axonal degeneration.",
    decision:
      "Confirmation would redefine the topographical spectrum of POLG-related neuropathies and highlight DRG-specific bioenergetic fragility.",
  },
  {
    tier: "ESTABLISHED",
    text: "POLG mutations are associated with parkinsonism and co-segregate with progressive external ophthalmoplegia, reflecting shared susceptibility of basal ganglia circuits to mitochondrial dysfunction.",
    source: "Structural and Molecular Basis for Mitochondrial Parkinsonism.",
  },
  {
    tier: "INFERRED",
    text: "Substantia nigra dopaminergic neurons exhibit high basal ROS production from dopamine metabolism and iron accumulation, rendering them uniquely vulnerable to respiratory chain impairment. The levodopa-unresponsive parkinsonism and asymmetric resting tremor likely reflect striatal-thalamic-cortical network dysfunction from chronic energy deficit, rather than primary alpha-synuclein pathology.",
  },
  {
    tier: "ESTABLISHED",
    text: "Seizures represent a major clinical feature in POLG-related disease across multiple age-stratified cohorts.",
    source: "PMID 32391929.",
  },
  {
    tier: "INFERRED",
    text: "The presence of COX-negative fibers indicates focal mtDNA instability within scattered myofibers. The absence of ragged-red fibers despite COX deficiency suggests compensatory mitochondrial biogenesis has not reached the morphological threshold for subsarcolemmal accumulation — a pattern seen in late-onset, slowly progressive mtDNA maintenance disorders. Normal bulk spectrophotometry is consistent with mosaic distribution: average enzyme activity remains within range because unaffected fibers compensate for deficient ones.",
  },
];

const investigations = [
  {
    heading: "Clinical priority",
    items: [
      {
        title:
          "Sodium valproate contraindication assessment and avoidance protocol",
        why: "Valproate inhibits carnitine uptake and mitochondrial fatty acid beta-oxidation, directly exacerbating mitochondrial dysfunction. In POLG carriers it carries a well-documented risk of fatal hepatotoxicity, hyperammonaemic encephalopathy, and rapid deterioration.",
        decision:
          "Absolute contraindication if POLG pathogenic variants are confirmed or strongly suspected clinically. Select alternative antiepileptic agents with neutral mitochondrial profiles immediately.",
      },
      {
        title: "Targeted POLG sequencing and mtDNA stability assay",
        why: "Next-generation sequencing of POLG coding regions plus Southern blot or long-range PCR for multiple mtDNA deletions on muscle DNA. Directly tests the leading hypothesis and distinguishes a primary maintenance defect from secondary mitochondrial dysfunction.",
        decision:
          "Two or more pathogenic/likely pathogenic POLG variants confirms the diagnosis; wild-type sequence shifts focus to alternative etiologies.",
      },
      {
        title: "Comprehensive ophthalmologic evaluation",
        why: "Dilated fundus exam, OCT of retina and optic nerve, formal visual fields. Differentiates pure extraocular muscle disease from syndromic mitochondrial retinopathy or optic neuropathy, refining the differential between CPEO-plus, Kearns-Sayre, and OPA1-related disease.",
        decision:
          "Retinal thinning or optic disc pallor would support syndromic disease or OPA1; preservation supports isolated CPEO-plus.",
      },
    ],
  },
  {
    heading: "Research and translational",
    items: [
      {
        title:
          "Single-fiber digital droplet PCR for mtDNA copy number and deletion mapping",
        why: "Quantifies the spatial distribution and heteroplasmy threshold of mtDNA deletions at single-cell level. Demonstrates mosaic segregation characteristic of POLG-related instability and provides a baseline for monitoring progression.",
      },
      {
        title:
          "Mitochondrial respiratory chain supercomplex assembly profiling",
        why: "Evaluates whether COX deficiency reflects isolated Complex IV assembly failure or broader supercomplex disassembly, informing mechanistic subclassification of POLG phenotypes.",
      },
    ],
  },
  {
    heading: "Model-proposed novel",
    items: [
      {
        title:
          "Dorsal root ganglion mitochondrial proteomics and metabolomic flux analysis",
        why: "Tests the hypothesis that DRG neurons exhibit unique vulnerability to mtDNA maintenance defects due to high local ROS burden. Mass spectrometry quantification of mitochondrial protein complexes, NAD+/NADH ratios, and succinate/fumarate flux in DRG tissue or iPSC-derived sensory neurons. Predicted: selective downregulation of Complex IV assembly factors and elevated lipid peroxidation in DRG models versus motor neuron controls. Would establish DRG-specific bioenergetic fragility as a distinct axis in adult-onset mitochondrial neuropathies.",
      },
    ],
  },
];

const sections = [
  { id: "case", label: "The case" },
  { id: "variant", label: "Variant interpretation" },
  { id: "pathway", label: "Reasoning pathway" },
  { id: "mechanism", label: "Mechanistic chain" },
  { id: "diagnosis", label: "Diagnosis" },
  { id: "change", label: "What would change it" },
  { id: "investigations", label: "Investigations" },
  { id: "adversarial", label: "Adversarial testing" },
];

const TIER = {
  ESTABLISHED: c.tierEstablished,
  INFERRED: c.tierInferred,
  HYPOTHESIS: c.tierHypothesis,
};

export default function POLGCpeoClinicalDemo() {
  const [active, setActive] = useState("case");

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        }),
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el)
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 100,
        behavior: "smooth",
      });
  };

  return (
    <div className={styles.page}>
      <div className={styles.background}>
        <div className={styles.glow} />
      </div>

      <div className={c.shell}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className={styles.backLink}>
            &larr; Back to Home
          </Link>

          <div className={c.header}>
            <span className={c.tag}>MitoAgent · Live Demo</span>
            <h1 className={c.title}>
              CPEO-plus —{" "}
              <span className={c.highlight}>
                a multi-system case scored with honest uncertainty
              </span>
            </h1>
            <p className={c.subtitle}>
              72-year-old man · no genetic testing yet · valproate safety
              question
            </p>
            <div className={c.statsRow}>
              <div className={c.statItem}>
                <span className={c.statLabel}>Confidence</span>
                <span className={c.statValue}>Medium</span>
              </div>
              <div className={c.statItem}>
                <span className={c.statLabel}>Support score</span>
                <span className={c.statValue}>7.59</span>
              </div>
              <div className={c.statItem}>
                <span className={c.statLabel}>Deployment</span>
                <span className={c.statValue}>Local</span>
              </div>
            </div>
          </div>
        </motion.div>

        <div className={c.layout}>
          <nav className={c.nav}>
            <div className={c.navInner}>
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`${c.navItem} ${active === s.id ? c.navActive : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </nav>

          <div className={c.content}>
            <section id="case" className={c.section}>
              <h2 className={c.sectionTitle}>The case</h2>
              <table className={c.dataTable}>
                <tbody>
                  {presentation.map((row) => (
                    <tr key={row.label}>
                      <td className={c.dataLabel}>{row.label}</td>
                      <td className={c.dataValue}>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section id="variant" className={c.section}>
              <h2 className={c.sectionTitle}>Variant interpretation</h2>
              <div className={c.assessment}>
                <p className={c.assessmentLabel}>
                  No genetic testing performed — diagnosis is presumptive
                </p>
                <p className={c.assessmentText}>
                  No pathogenic variants have been identified, so diagnostic
                  certainty remains presumptive. The absence of genetic data
                  does not exclude a mitochondrial etiology, since phenotypic
                  expression depends on mutational load, tissue-specific
                  segregation, and nuclear-mitochondrial interactions. Wild-type
                  sequences for maintenance genes (POLG, TWNK, DGUOK, MPV17,
                  OPA1) would lower the probability of a primary mtDNA
                  maintenance disorder and shift focus to secondary dysfunction
                  from ageing or comorbidity. Biallelic pathogenic POLG variants
                  would confirm a nuclear-encoded replication defect. Clinical
                  and histological findings are the sole basis for hypothesis
                  generation at this stage.
                </p>
              </div>
            </section>

            <section id="pathway" className={c.section}>
              <h2 className={c.sectionTitle}>Reasoning pathway</h2>
              <PathwayGraph graph={pathway} />
            </section>

            <section id="mechanism" className={c.section}>
              <h2 className={c.sectionTitle}>Mechanistic chain</h2>
              {mechanism.map((m, i) => (
                <div key={i} className={c.mechItem}>
                  <span
                    className={`${c.tier} ${TIER[m.tier] || c.tierInferred}`}
                  >
                    {m.tier}
                  </span>
                  <p className={c.mechText}>{m.text}</p>
                  {m.source && (
                    <p className={c.mechSource}>Source: {m.source}</p>
                  )}
                  {m.prediction && (
                    <div className={c.prediction}>
                      <p>
                        <strong>Testable prediction:</strong> {m.prediction}
                      </p>
                      {m.decision && (
                        <p>
                          <strong>Decision rule:</strong> {m.decision}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </section>

            <section id="diagnosis" className={c.section}>
              <h2 className={c.sectionTitle}>Diagnosis</h2>
              <div
                className={c.assessment}
                style={{ borderLeftColor: "hsl(var(--primary))" }}
              >
                <p
                  className={c.assessmentLabel}
                  style={{ color: "hsl(var(--primary))" }}
                >
                  Leading diagnosis — CPEO-plus (POLG-related) · Medium
                  confidence
                </p>
                <p className={c.assessmentText}>
                  Driven by the classic triad of progressive external
                  ophthalmoplegia, ptosis, and multi-system neurological
                  involvement, supported by muscle biopsy evidence of
                  COX-negative fibers indicating mtDNA instability. Adult-onset
                  POLG disease frequently presents with ataxia, neuropathy,
                  seizures, and parkinsonism. Confidence is moderated by
                  atypical features: the non-length-dependent sensory
                  ganglionopathy deviates from the classical mixed sensorimotor
                  axonal neuropathy of POLG disease; the absence of ragged-red
                  fibers and normal bulk respiratory chain activity reduce
                  histological specificity; and molecular confirmation is
                  pending. The most cohesive unifying framework, but requires
                  genetic validation.
                </p>
              </div>
              <div className={c.mechItem} style={{ marginTop: "1.5rem" }}>
                <span className={`${c.tier} ${c.tierHypothesis}`}>
                  Coverage scoring
                </span>
                <p className={c.mechText}>
                  Support 10.0 + coverage 0.37 − contradictions 0.0 − complexity
                  2.78 = <strong>7.59</strong>. Findings explained: 1 of 22. The
                  complexity penalty reflects the cost of unifying a highly
                  heterogeneous, multi-system presentation with overlapping
                  neurodegenerative, metabolic, and age-related processes — the
                  model does not hide that most findings remain only partially
                  mapped.
                </p>
              </div>
            </section>

            <section id="change" className={c.section}>
              <h2 className={c.sectionTitle}>
                What would change this diagnosis
              </h2>
              <div className={c.mechItem}>
                <span className={`${c.tier} ${c.tierEstablished}`}>
                  Confirmatory
                </span>
                <p className={c.mechText}>
                  Biallelic pathogenic or likely pathogenic POLG variants via
                  targeted or whole-exome sequencing, with multiple mtDNA
                  deletions or significant depletion in muscle (above 10%
                  deleted mtDNA molecules on Southern blot or qPCR relative to
                  wild-type).
                </p>
              </div>
              <div className={c.mechItem}>
                <span className={`${c.tier} ${c.tierHypothesis}`}>
                  Refuting
                </span>
                <p className={c.mechText}>
                  Wild-type POLG with no pathogenic variants, combined with
                  either a monogenic alternative explaining the full phenotype
                  (e.g. primary sensory neuronopathy, or confirmed
                  autoimmune/paraneoplastic ganglionopathy with CSF
                  pleocytosis), or normal mtDNA integrity on high-resolution
                  mapping alongside a distinct neurodegenerative pathology (Lewy
                  body disease, FTLD) on neuropathological correlation.
                </p>
              </div>
            </section>

            <section id="investigations" className={c.section}>
              <h2 className={c.sectionTitle}>Next investigations</h2>
              {investigations.map((group, gi) => (
                <div key={gi} className={c.invGroup}>
                  <p className={c.invHeading}>{group.heading}</p>
                  {group.items.map((inv, ii) => (
                    <div key={ii} className={c.invCard}>
                      <p className={c.invTitle}>{inv.title}</p>
                      {inv.why && (
                        <p className={c.invWhy}>
                          <strong>Why:</strong> {inv.why}
                        </p>
                      )}
                      {inv.decision && (
                        <p className={c.invDecision}>
                          <strong>Decision rule:</strong> {inv.decision}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </section>

            <section id="adversarial" className={c.section}>
              <h2 className={c.sectionTitle}>Adversarial testing</h2>
              <div className={c.advCard}>
                <div className={c.advHead}>
                  <span className={c.advHyp}>CPEO-plus (POLG-related)</span>
                  <span className={`${c.advResult} ${c.resultSurvives}`}>
                    Weakened, survives
                  </span>
                </div>
                <p className={c.advLine}>
                  <strong>Strongest argument against:</strong> A pure
                  non-length-dependent sensory ganglionopathy with asymmetric,
                  levodopa-unresponsive parkinsonism is highly atypical for POLG
                  disease, whose neuropathies are classically length-dependent
                  and mixed sensorimotor with preserved motor conduction. The
                  absence of ragged-red fibers alongside isolated COX deficiency
                  and normal bulk activity points instead toward age-related
                  mitochondrial decay or a distinct neurodegenerative process.
                </p>
                <p className={c.advLine}>
                  <strong>Why it survives:</strong> Not refuted. Genetic testing
                  has not been performed, and histological findings remain
                  compatible with early or mosaic mtDNA instability. The
                  hypothesis retains explanatory power for the core CPEO triad
                  and systemic features but loses specificity.
                </p>
                <p className={c.advLine}>
                  <strong>Best refutation test:</strong> Comprehensive POLG
                  sequencing paired with high-resolution mtDNA
                  deletion/depletion quantification in muscle. Wild-type POLG
                  with intact mtDNA architecture would definitively exclude the
                  diagnosis.
                </p>
              </div>
            </section>

            <div className={c.disclaimer}>
              Research tool only. MitoAgent is not a diagnostic system and does
              not constitute medical advice. All clinical decisions must be made
              by qualified healthcare professionals. Outputs should be
              independently verified by domain experts. This demo uses a
              synthetic case for illustrative purposes.
            </div>

            <div className={styles.cta}>
              <p className={styles.ctaText}>
                Run your own case through MitoAgent.
              </p>
              <Link to="/playground" className={styles.ctaButton}>
                Apply for Early Access &rarr;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
